import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import type { ShareCoverageResponse } from "src/api/shares";
import RetryableError from "src/views/assistant/projects/components/RetryableError";

/** A dataset share in flight from the dialog, for the button it came from. */
export interface DatasetShareProgress {
  datasetId: number;
  done: number;
  total: number;
}

interface CoverageDatasetsBlockProps {
  coverage: ShareCoverageResponse | undefined;
  isLoading: boolean;
  error?: unknown;
  onRetry?: () => void;
  tid: string;
  /** Grant View only on the dataset to every roster member short of it. */
  onShareWithMissing: (datasetId: number, userIds: number[]) => void;
  progress: DatasetShareProgress | null;
  /** A dataset grant from this block or the access list is in flight. */
  busy: boolean;
}

export const hiddenDatasetLabel = "1 dataset you cannot read";

/**
 * The datasets an agent works from and how many of the share roster can read
 * each, computed by the backend at open time. Where the caller manages a
 * dataset the row offers to close the gap in place; elsewhere it names who
 * can. A dataset the caller cannot read is counted but never named.
 */
const CoverageDatasetsBlock: React.FC<CoverageDatasetsBlockProps> = ({
  coverage,
  isLoading,
  error,
  onRetry,
  tid,
  onShareWithMissing,
  progress,
  busy,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }} data-testid={`${tid}-coverage-loading`}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rounded" height={40} sx={{ my: 0.5 }} />
      </Box>
    );
  }
  if (error != null) {
    return (
      <Box sx={{ mb: 2 }}>
        <RetryableError
          message="Couldn't compute dataset coverage."
          onRetry={onRetry}
          testId={`${tid}-coverage-error`}
        />
      </Box>
    );
  }
  if (!coverage) return null;

  const people = coverage.people_total;
  return (
    <Box sx={{ mb: 2 }} data-testid={`${tid}-coverage`}>
      <Typography variant="subtitle2">
        Datasets this agent works from
      </Typography>
      {coverage.datasets_total === 0 && (
        <Typography variant="caption" color="text.secondary">
          This agent works from no datasets.
        </Typography>
      )}
      {coverage.datasets.length > 0 && (
        <List dense disablePadding>
          {coverage.datasets.map((row, index) => {
            const hidden = row.dataset_id === null;
            const missing = row.missing_user_ids.length;
            const rowId = hidden ? `hidden-${index}` : `${row.dataset_id}`;
            const sharing =
              !hidden && progress?.datasetId === row.dataset_id
                ? progress
                : null;
            return (
              <ListItem
                key={rowId}
                sx={{ px: 0, alignItems: "flex-start" }}
                data-testid={`${tid}-coverage-dataset-${rowId}`}
              >
                <ListItemText
                  primary={hidden ? hiddenDatasetLabel : row.name}
                  secondaryTypographyProps={{ component: "div" }}
                  secondary={
                    <Stack spacing={0.25}>
                      <Typography variant="caption" color="text.secondary">
                        {hidden
                          ? "Its name and owner are not shown to you"
                          : row.caller_can_manage
                            ? "You manage this dataset"
                            : `Owner: ${row.owner_name ?? "unknown"}`}
                      </Typography>
                      {!hidden && !row.caller_can_manage && missing === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Only its owner can change who reads it
                        </Typography>
                      )}
                    </Stack>
                  }
                />
                <Stack
                  alignItems="flex-end"
                  spacing={0.5}
                  sx={{ minWidth: 160 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    data-testid={`${tid}-coverage-count-${rowId}`}
                  >
                    {row.readable_count} of {people}
                  </Typography>
                  {hidden ? (
                    <Typography variant="caption" color="text.secondary">
                      Only its owner can change who reads it
                    </Typography>
                  ) : row.caller_can_manage ? (
                    missing > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() =>
                          onShareWithMissing(
                            row.dataset_id!,
                            row.missing_user_ids,
                          )
                        }
                        data-testid={`${tid}-coverage-share-${rowId}`}
                        startIcon={
                          sharing ? <CircularProgress size={14} /> : undefined
                        }
                      >
                        {sharing
                          ? `Sharing… ${sharing.done} of ${sharing.total}`
                          : `Share with the ${missing} who can't`}
                      </Button>
                    )
                  ) : (
                    row.readable_count < people && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        data-testid={`${tid}-coverage-ask-${rowId}`}
                      >
                        Ask {row.owner_name ?? "its owner"} to share it
                      </Typography>
                    )
                  )}
                </Stack>
              </ListItem>
            );
          })}
        </List>
      )}
      {coverage.limits.members_truncated && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`${tid}-coverage-truncated`}
        >
          Coverage computed for the first {coverage.limits.member_cap} people on
          this list.
        </Typography>
      )}
      {coverage.limits.datasets_truncated && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-testid={`${tid}-coverage-datasets-truncated`}
        >
          Coverage computed for the first {coverage.limits.dataset_cap} datasets
          this agent works from.
        </Typography>
      )}
    </Box>
  );
};

export default CoverageDatasetsBlock;
